import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import {
  defaultColDef,
  getDefaultRowIndexColumnConfig,
} from "src/utils/grid/gridStyleUtils";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { formatNumber } from "src/utils/number/numberUtils";
import { formatDateMonth, formatDateYear4 } from "src/utils/string/stringUtil";
import {
  defaultPRD1020_R01Dto,
  PRD1020_R01Dto,
} from "./Dto_PRD1020_R01";
import { usePRD1020_R01Store } from "./Store_PRD1020_R01";

interface Table_PRD1020_R01Props {
  gridRef: React.RefObject<AgGridReact<PRD1020_R01Dto> | null>;
}

const COLUMN_STATE_KEY = "PRD1020_R01-grid-column-state";

/**
 * 생산계획과 생산실적의 월별 비교 결과를 표시하는 조회 전용 Grid이다.
 * 행 데이터의 병합과 행별 실적률 계산은 백엔드에서 완료하며, 이 컴포넌트는
 * 표시 형식, 사용자 컬럼 상태, 스크롤 위치와 화면 하단 합계만 관리한다.
 */
export const Table_PRD1020_R01 = ({ gridRef }: Table_PRD1020_R01Props) => {
  const {
    PRD1020_R01List,
    columnState,
    setColumnState,
    scrollTop,
    setScrollTop,
  } = usePRD1020_R01Store();

  const [, setHoveredRowIndex] = useState<number | null>(null);

  /*
   * 공통 Grid 훅의 역할:
   * - Content에서 부여한 valueIndex를 AG Grid의 단일 행 ID로 사용한다.
   * - 조회 결과를 복사하면서 rowIndex를 추가한 paddedGridData를 만든다.
   * - 공통 순번 컬럼이 사용할 행 번호 계산 함수를 제공한다.
   * 이 화면은 행 선택 기능이 없으므로 selectedRow는 전달하지 않는다.
   */
  const { getRowId, settingRowIndex, paddedGridData } =
    useGridSettingContent<PRD1020_R01Dto>({
      gridRef,
      key: "valueIndex",
      gridData: PRD1020_R01List,
      defaultGridData: defaultPRD1020_R01Dto,
      selectedRow: undefined,
    });

  /*
   * headerNames는 화면 헤더 표시뿐 아니라 저장된 columnState를 다시
   * ColDef로 복원할 때도 사용한다. key는 각 컬럼의 colId와 일치해야 한다.
   */
  const headerNames = useMemo(
    () => ({
      rowIndex: "",
      yearMonth: "생산년월",
      compNm: "사업장명",
      itemCd: "품목코드",
      itemNm: "품목명",
      itemSpec: "규격",
      itemUnit: "단위",
      planQty: "생산계획량",
      resultQty: "생산실적량",
      achievementRate: "실적률 (%)",
      remark: "비고",
    }),
    [],
  );

  /*
   * onlyTouchGridStyle은 조회 전용 Grid의 정렬을 결정하는 공통 팩토리다.
   * 첫 번째 인자는 현재 컬럼 필드이며, 두 번째 인자는 pinned bottom 행에서
   * 합계 문구를 중앙 정렬할 컬럼이다. 이 화면의 "계 (n건)" 문구가 compNm에
   * 들어가므로 centerBottomKey로 compNm을 전달한다.
   */
  const getCellStyle = useCallback(
    (field: keyof typeof headerNames) => onlyTouchGridStyle(field, "compNm"),
    [],
  );

  /*
   * baseColumnDefs는 설계 화면에 표시되는 고정 컬럼 정의다.
   * 모든 업무 컬럼은 조회 전용이며, getDefaultRowIndexColumnConfig가 공통
   * 순번 컬럼을 앞에 추가한다. field와 colId는 PRD1020_R01Dto의 실제 필드와
   * 동일하게 유지하여 컬럼 상태 저장·복원이 같은 키를 사용하도록 한다.
   */
  const baseColumnDefs = useMemo<ColDef<PRD1020_R01Dto>[]>(
    () => [
      ...getDefaultRowIndexColumnConfig(),
      {
        colId: "yearMonth",
        field: "yearMonth",
        headerName: headerNames.yearMonth,
        width: 110,
        editable: false,
        cellStyle: getCellStyle("yearMonth"),
        valueFormatter: ({ data }) => {
          // 서버의 YYYYMM과 이미 포맷된 YYYY-MM을 모두 화면의 YYYY-MM으로 통일한다.
          const yearMonth = data?.yearMonth;
          if (!yearMonth) return "";
          if (/^\d{6}$/.test(yearMonth)) {
            return `${yearMonth.substring(0, 4)}-${yearMonth.substring(4, 6)}`;
          }
          return `${formatDateYear4(new Date(yearMonth))}-${formatDateMonth(
            new Date(yearMonth),
          )}`;
        },
      },
      {
        colId: "compNm",
        field: "compNm",
        headerName: headerNames.compNm,
        width: 150,
        editable: false,
        cellStyle: getCellStyle("compNm"),
      },
      {
        colId: "itemCd",
        field: "itemCd",
        headerName: headerNames.itemCd,
        width: 130,
        editable: false,
        cellStyle: getCellStyle("itemCd"),
      },
      {
        colId: "itemNm",
        field: "itemNm",
        headerName: headerNames.itemNm,
        width: 150,
        editable: false,
        cellStyle: getCellStyle("itemNm"),
      },
      {
        colId: "itemSpec",
        field: "itemSpec",
        headerName: headerNames.itemSpec,
        width: 130,
        editable: false,
        cellStyle: getCellStyle("itemSpec"),
      },
      {
        colId: "itemUnit",
        field: "itemUnit",
        headerName: headerNames.itemUnit,
        width: 80,
        editable: false,
        cellStyle: getCellStyle("itemUnit"),
      },
      {
        colId: "planQty",
        field: "planQty",
        headerName: headerNames.planQty,
        width: 130,
        editable: false,
        type: "numericColumn",
        headerClass: aggridHeaderClass,
        cellStyle: getCellStyle("planQty"),
        // 계획 자체가 없는 결과 전용 행은 숫자 0과 구분하기 위해 빈칸으로 표시한다.
        valueFormatter: ({ value, data, node }) =>
          node?.rowPinned || data?.hasPlan ? formatNumber(value ?? 0) : "",
      },
      {
        colId: "resultQty",
        field: "resultQty",
        headerName: headerNames.resultQty,
        width: 130,
        editable: false,
        type: "numericColumn",
        headerClass: aggridHeaderClass,
        cellStyle: getCellStyle("resultQty"),
        // 실적 자체가 없는 계획 전용 행은 숫자 0과 구분하기 위해 빈칸으로 표시한다.
        valueFormatter: ({ value, data, node }) =>
          node?.rowPinned || data?.hasResult ? formatNumber(value ?? 0) : "",
      },
      {
        colId: "achievementRate",
        field: "achievementRate",
        headerName: headerNames.achievementRate,
        width: 120,
        editable: false,
        type: "numericColumn",
        headerClass: aggridHeaderClass,
        cellStyle: getCellStyle("achievementRate"),
        valueFormatter: ({ value }) => Number(value ?? 0).toFixed(1),
      },
      {
        colId: "remark",
        field: "remark",
        headerName: headerNames.remark,
        minWidth: 140,
        flex: 1,
        editable: false,
        cellStyle: getCellStyle("remark"),
      },
    ],
    [getCellStyle, headerNames],
  );

  // 최초 컬럼 상태는 baseColumnDefs에서 만들고, 이후 순서·너비·숨김 상태를 복원한다.
  const defaultColumnState = useMemo(
    () => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
    [baseColumnDefs],
  );

  /*
   * columnState가 있으면 공통 GridCustomColumnDefs가 저장된 컬럼 순서와 너비를
   * baseColumnDefs에 병합한다. 실제 valueFormatter와 cellStyle은 base 정의를
   * 유지하므로 사용자 설정을 복원해도 화면의 계산·표시 규칙은 바뀌지 않는다.
   */
  const columnDefs = useMemo(
    () =>
      GridCustomColumnDefs<PRD1020_R01Dto>({
        baseColumnDefs,
        columnState,
        headerNames,
        fallbackHeaderClass: aggridHeaderClass,
      }),
    [baseColumnDefs, columnState, headerNames, settingRowIndex],
  );

  /*
   * 하단 합계는 현재 조회된 병합 목록 전체를 대상으로 계산한다.
   * - 건수: 계획/실적 병합 후 화면에 표시되는 행 수
   * - 생산계획량·생산실적량: 각 컬럼의 단순 합계
   * - 전체 실적률: 총실적 / 총계획 × 100
   * 총계획이 0이면 화면 요구사항에 따라 100.0%로 표시한다.
   * hasPlan/hasResult를 true로 두는 이유는 합계의 0도 빈칸이 아닌 숫자로
   * 표시하기 위해서다.
   */
  const pinnedBottomRowData = useMemo<PRD1020_R01Dto[]>(() => {
    const totalPlanQty = PRD1020_R01List.reduce(
      (sum, row) => sum + (row.planQty ?? 0),
      0,
    );
    const totalResultQty = PRD1020_R01List.reduce(
      (sum, row) => sum + (row.resultQty ?? 0),
      0,
    );

    return [
      {
        ...defaultPRD1020_R01Dto,
        compNm: `계 (${PRD1020_R01List.length}건)`,
        planQty: totalPlanQty,
        resultQty: totalResultQty,
        achievementRate:
          totalPlanQty === 0 ? 100 : (totalResultQty / totalPlanQty) * 100,
        hasPlan: true,
        hasResult: true,
      },
    ];
  }, [PRD1020_R01List]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/*
        GridContextWrapper는 컬럼 설정 메뉴와 localStorage 상태를 관리한다.
        조회 전용 화면이므로 데이터 변경 이력과 별도 메뉴 항목은 사용하지 않는다.
      */}
      <GridContextWrapper<PRD1020_R01Dto>
        enableCellCalculation
        gridRef={gridRef}
        paddedGridData={paddedGridData}
        setPaddedGridData={undefined}
        defaultRowData={defaultPRD1020_R01Dto}
        baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
        defaultColumnState={defaultColumnState}
        setColumnState={setColumnState}
        localStorageKey={COLUMN_STATE_KEY}
        enableDataHistory={false}
        menuItems={undefined}
      >
        {/*
          Grid_Touch는 조회 전용 공통 Grid이다. Store의 columnState·scrollTop과
          연결하여 화면 내 재렌더링 후에도 컬럼 상태와 스크롤 위치를 유지한다.
        */}
        <Grid_Touch<PRD1020_R01Dto>
          ref={gridRef}
          rowData={paddedGridData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={getRowId}
          onCellKeyDown={undefined}
          pinnedBottomRowData={pinnedBottomRowData}
          setHoveredRowIndex={setHoveredRowIndex}
          columnStateKey={COLUMN_STATE_KEY}
          setColumnState={setColumnState}
          scrollTop={scrollTop}
          setScrollTop={setScrollTop}
        />
      </GridContextWrapper>
    </div>
  );
};
